import React, { useMemo, useRef, useState } from "react";

type PaletteCategory =
  | "Tables"
  | "Displays"
  | "Electronics"
  | "Storage"
  | "Decor"
  | "Annotations";

type PaletteItem = {
  name: string;
  category: PaletteCategory;
  width: number;
  height: number;
  color: string;
};

type BoothItem = PaletteItem & {
  id: string;
  x: number;
  y: number;
  rotation: 0 | 90;
};

type Template = {
  title: string;
  description: string;
  items: Array<Omit<BoothItem, "id">>;
};

const palette: PaletteItem[] = [
  { category: "Tables", name: "8ft Table", width: 8, height: 2.5, color: "#2563eb" },
  { category: "Tables", name: "6ft Table", width: 6, height: 2.5, color: "#2563eb" },
  { category: "Tables", name: "4ft Table", width: 4, height: 2, color: "#2563eb" },
  { category: "Tables", name: "Payment Counter", width: 2, height: 1, color: "#0f766e" },
  { category: "Tables", name: "Demo Counter", width: 6, height: 2, color: "#0f766e" },
  { category: "Displays", name: "Gridwall Panel", width: 2, height: 0.25, color: "#7c3aed" },
  { category: "Displays", name: "Slatwall Panel", width: 3, height: 0.25, color: "#7c3aed" },
  { category: "Displays", name: "Jewelry Case", width: 4, height: 1.8, color: "#9333ea" },
  { category: "Displays", name: "Cube Shelf", width: 3, height: 1.5, color: "#9333ea" },
  { category: "Displays", name: "Card Spinner", width: 1.5, height: 1.5, color: "#9333ea" },
  { category: "Electronics", name: "POS Terminal", width: 1.5, height: 1, color: "#ea580c" },
  { category: "Electronics", name: "Power Strip", width: 1, height: 0.3, color: "#ea580c" },
  { category: "Electronics", name: "TV Stand", width: 3, height: 1.5, color: "#ea580c" },
  { category: "Storage", name: "Wire Shelf", width: 4, height: 1.5, color: "#475569" },
  { category: "Storage", name: "Storage Crate", width: 3, height: 2, color: "#475569" },
  { category: "Storage", name: "Cooler", width: 2, height: 1.5, color: "#475569" },
  { category: "Storage", name: "Pipe and Drape", width: 10, height: 0.5, color: "#64748b" },
  { category: "Decor", name: "Banner Stand", width: 2.8, height: 1, color: "#db2777" },
  { category: "Decor", name: "Rug 5x8", width: 8, height: 5, color: "#db2777" },
  { category: "Decor", name: "A-Frame Sign", width: 2, height: 1.5, color: "#db2777" },
  { category: "Decor", name: "Brand Sign", width: 3, height: 0.3, color: "#db2777" },
  { category: "Annotations", name: "Zone Marker", width: 3, height: 3, color: "#16a34a" },
  { category: "Annotations", name: "Flow Arrow", width: 2, height: 0.5, color: "#16a34a" },
  { category: "Annotations", name: "Text Label", width: 2, height: 1, color: "#16a34a" },
];

const templates: Template[] = [
  {
    title: "Classic Market Table",
    description: "6ft table with banner, chair, and checkout counter.",
    items: [
      { ...palette[1], x: 2, y: 7, rotation: 0 },
      { ...palette[17], x: 3.5, y: 6.2, rotation: 0 },
      { ...palette[3], x: 7.5, y: 7.5, rotation: 0 },
      { ...palette[22], x: 7.5, y: 3, rotation: 0 },
    ],
  },
  {
    title: "L-Shape Gallery",
    description: "Wall displays around the back and one side with a front table.",
    items: [
      { ...palette[5], x: 0.5, y: 0.4, rotation: 0 },
      { ...palette[5], x: 3, y: 0.4, rotation: 0 },
      { ...palette[5], x: 5.5, y: 0.4, rotation: 0 },
      { ...palette[5], x: 8, y: 0.4, rotation: 90 },
      { ...palette[2], x: 2.5, y: 7.6, rotation: 0 },
      { ...palette[18], x: 1, y: 3.5, rotation: 0 },
    ],
  },
  {
    title: "Jewelry Showcase",
    description: "Cases, POS, and controlled flow for higher-value inventory.",
    items: [
      { ...palette[7], x: 1, y: 7.6, rotation: 0 },
      { ...palette[7], x: 5.2, y: 7.6, rotation: 0 },
      { ...palette[9], x: 4.4, y: 4.3, rotation: 0 },
      { ...palette[10], x: 7.8, y: 4.5, rotation: 0 },
      { ...palette[17], x: 3.5, y: 0.8, rotation: 0 },
    ],
  },
];

const categories: PaletteCategory[] = [
  "Tables",
  "Displays",
  "Electronics",
  "Storage",
  "Decor",
  "Annotations",
];

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(min, max), Math.max(min, value));

const snap = (value: number) => Math.round(value * 2) / 2;

const itemSize = (item: BoothItem | Omit<BoothItem, "id">) =>
  item.rotation === 90
    ? { width: item.height, height: item.width }
    : { width: item.width, height: item.height };

const makeId = () =>
  `booth-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function BoothLayoutPlanner() {
  const [boothWidth, setBoothWidth] = useState(10);
  const [boothHeight, setBoothHeight] = useState(10);
  const [boothWidthInput, setBoothWidthInput] = useState("10");
  const [boothHeightInput, setBoothHeightInput] = useState("10");
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<PaletteCategory | "All">("All");
  const [items, setItems] = useState<BoothItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const cellPx = Math.max(
    34,
    Math.min(64, Math.floor(720 / Math.max(boothWidth, boothHeight))),
  );
  const gridWidth = boothWidth * cellPx;
  const gridHeight = boothHeight * cellPx;
  const topLabels = Array.from({ length: Math.floor(boothWidth) }, (_, index) => index);
  const sideLabels = Array.from({ length: Math.floor(boothHeight) }, (_, index) => index);

  const updateBoothDimension = (
    rawValue: string,
    setInput: React.Dispatch<React.SetStateAction<string>>,
    setDimension: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setInput(rawValue);
    if (rawValue.trim() === "") return;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    setDimension(clamp(parsed, 3, 30));
  };

  const filteredPalette = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return palette.filter((item) => {
      const categoryMatches =
        activeCategory === "All" || item.category === activeCategory;
      const textMatches =
        !normalized ||
        `${item.name} ${item.category}`.toLowerCase().includes(normalized);
      return categoryMatches && textMatches;
    });
  }, [activeCategory, query]);

  const selectedItem = items.find((item) => item.id === selectedId) || null;

  const addItem = (base: PaletteItem, x = 0.5, y = 0.5) => {
    const next: BoothItem = {
      ...base,
      id: makeId(),
      x,
      y,
      rotation: 0,
    };
    const size = itemSize(next);
    next.x = clamp(next.x, 0, boothWidth - size.width);
    next.y = clamp(next.y, 0, boothHeight - size.height);
    setItems((current) => [...current, next]);
    setSelectedId(next.id);
  };

  const applyTemplate = (template: Template) => {
    setItems(
      template.items.map((item) => ({
        ...item,
        id: makeId(),
      })),
    );
    setSelectedId(null);
  };

  const updateItem = (id: string, updater: (item: BoothItem) => BoothItem) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const moveSelected = (dx: number, dy: number) => {
    if (!selectedId) return;
    updateItem(selectedId, (item) => {
      const size = itemSize(item);
      return {
        ...item,
        x: clamp(snap(item.x + dx), 0, boothWidth - size.width),
        y: clamp(snap(item.y + dy), 0, boothHeight - size.height),
      };
    });
  };

  const rotateSelected = () => {
    if (!selectedId) return;
    updateItem(selectedId, (item) => {
      const next = {
        ...item,
        rotation: (item.rotation === 0 ? 90 : 0) as 0 | 90,
      };
      const size = itemSize(next);
      return {
        ...next,
        x: clamp(next.x, 0, boothWidth - size.width),
        y: clamp(next.y, 0, boothHeight - size.height),
      };
    });
  };

  const duplicateSelected = () => {
    if (!selectedItem) return;
    const size = itemSize(selectedItem);
    const copy: BoothItem = {
      ...selectedItem,
      id: makeId(),
      x: clamp(selectedItem.x + 0.5, 0, boothWidth - size.width),
      y: clamp(selectedItem.y + 0.5, 0, boothHeight - size.height),
    };
    setItems((current) => [...current, copy]);
    setSelectedId(copy.id);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setItems((current) => current.filter((item) => item.id !== selectedId));
    setSelectedId(null);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingId || !gridRef.current) return;
    const bounds = gridRef.current.getBoundingClientRect();
    const target = items.find((item) => item.id === draggingId);
    if (!target) return;
    const size = itemSize(target);
    const nextX = snap((event.clientX - bounds.left) / cellPx - size.width / 2);
    const nextY = snap((event.clientY - bounds.top) / cellPx - size.height / 2);
    updateItem(draggingId, (item) => ({
      ...item,
      x: clamp(nextX, 0, boothWidth - size.width),
      y: clamp(nextY, 0, boothHeight - size.height),
    }));
  };

  const score = useMemo(() => {
    let value = 100;
    const frontBlocked = items.some((item) => item.y + itemSize(item).height > boothHeight - 0.5);
    const tooDense =
      items.reduce((sum, item) => {
        const size = itemSize(item);
        return sum + size.width * size.height;
      }, 0) /
        (boothWidth * boothHeight) >
      0.55;
    const hasCheckout = items.some((item) =>
      /payment|pos|counter/i.test(item.name),
    );
    if (frontBlocked) value -= 15;
    if (tooDense) value -= 20;
    if (!hasCheckout) value -= 10;
    return clamp(value, 0, 100);
  }, [items, boothHeight, boothWidth]);

  const scoreLabel =
    score >= 85 ? "Ready to try" : score >= 65 ? "Needs a walkthrough" : "Too tight";

  return (
    <main className="min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-6">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <a href="/tools" className="text-sm font-semibold text-blue-700">
              Tools
            </a>
            <h1 className="mt-1 text-3xl font-bold text-slate-950">
              Booth Layout Planner
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Build a scaled booth plan before you pack. Add tables, displays,
              storage, signs, and checkout zones to a snap-to-grid floor plan.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={() => window.print()}
            >
              Print plan
            </button>
            <button
              type="button"
              className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700"
              onClick={() => {
                setItems([]);
                setSelectedId(null);
              }}
            >
              Clear
            </button>
          </div>
        </header>

        <section className="grid gap-3 lg:grid-cols-[260px_minmax(680px,1fr)_240px]">
          <aside className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm print:hidden">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Booth size
              </h2>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <label className="text-xs font-semibold text-slate-600">
                  Width ft
                  <input
                    type="number"
                    min={3}
                    max={30}
                    value={boothWidthInput}
                    onChange={(event) =>
                      updateBoothDimension(
                        event.target.value,
                        setBoothWidthInput,
                        setBoothWidth,
                      )
                    }
                    onBlur={() => {
                      if (!boothWidthInput.trim()) {
                        setBoothWidthInput(String(boothWidth));
                      }
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Depth ft
                  <input
                    type="number"
                    min={3}
                    max={30}
                    value={boothHeightInput}
                    onChange={(event) =>
                      updateBoothDimension(
                        event.target.value,
                        setBoothHeightInput,
                        setBoothHeight,
                      )
                    }
                    onBlur={() => {
                      if (!boothHeightInput.trim()) {
                        setBoothHeightInput(String(boothHeight));
                      }
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
            </div>

            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Templates
              </h2>
              <div className="mt-2 space-y-2">
                {templates.map((template) => (
                  <button
                    key={template.title}
                    type="button"
                    onClick={() => applyTemplate(template)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-left hover:border-blue-300 hover:bg-blue-50"
                  >
                    <div className="text-sm font-bold text-slate-900">
                      {template.title}
                    </div>
                    <div className="mt-1 text-xs text-slate-600">
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Add items
              </h2>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search tables, signs, shelves..."
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <div className="mt-2 flex flex-wrap gap-1">
                {(["All", ...categories] as Array<PaletteCategory | "All">).map(
                  (category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setActiveCategory(category)}
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        activeCategory === category
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {category}
                    </button>
                  ),
                )}
              </div>
              <div className="mt-3 max-h-[38vh] space-y-2 overflow-y-auto pr-1">
                {filteredPalette.map((item) => (
                  <button
                    key={`${item.category}-${item.name}`}
                    type="button"
                    onClick={() => addItem(item)}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-blue-300 hover:bg-blue-50"
                  >
                    <span>
                      <span className="block text-sm font-semibold text-slate-900">
                        {item.name}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {item.width}' x {item.height}' · {item.category}
                      </span>
                    </span>
                    <span
                      className="h-4 w-4 rounded"
                      style={{ backgroundColor: item.color }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-bold text-slate-950">
                  The Show Floor
                </h2>
                <p className="text-xs text-slate-500">
                  {boothWidth}' x {boothHeight}' · {items.length} item
                  {items.length === 1 ? "" : "s"} · 1 square = 1 ft
                </p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                Score {score}/100 · {scoreLabel}
              </div>
            </div>

            <div
              className="relative mx-auto"
              style={{
                width: gridWidth + 54,
                height: gridHeight + 50,
              }}
            >
              <div
                className="absolute left-7 top-0 grid text-center text-xs font-medium text-slate-500"
                style={{
                  gridTemplateColumns: `repeat(${topLabels.length}, ${cellPx}px)`,
                  width: gridWidth,
                }}
              >
                {topLabels.map((label) => (
                  <span key={label}>{label}'</span>
                ))}
              </div>
              <div
                className="absolute left-0 top-6 grid text-right text-xs font-medium text-slate-500"
                style={{
                  gridTemplateRows: `repeat(${sideLabels.length}, ${cellPx}px)`,
                  height: gridHeight,
                  width: 22,
                }}
              >
                {sideLabels.map((label) => (
                  <span key={label} className="pr-1">
                    {label}'
                  </span>
                ))}
              </div>
              <div
                ref={gridRef}
                onPointerMove={handlePointerMove}
                onPointerUp={() => setDraggingId(null)}
                onPointerLeave={() => setDraggingId(null)}
                className="absolute left-7 top-6 touch-none border border-slate-200 bg-white"
                style={{
                  width: gridWidth,
                  height: gridHeight,
                  backgroundImage:
                    "linear-gradient(to right, #dfe7ef 1px, transparent 1px), linear-gradient(to bottom, #dfe7ef 1px, transparent 1px)",
                  backgroundSize: `${cellPx}px ${cellPx}px`,
                }}
              >
                {items.map((item) => {
                  const size = itemSize(item);
                  const selected = selectedId === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onPointerDown={(event) => {
                        event.currentTarget.setPointerCapture(event.pointerId);
                        setSelectedId(item.id);
                        setDraggingId(item.id);
                      }}
                      className={`absolute z-10 rounded-md border px-2 text-center text-[11px] font-bold leading-tight text-white shadow-sm transition ${
                        selected
                          ? "border-slate-950 ring-2 ring-blue-400"
                          : "border-white/60"
                      }`}
                      style={{
                        left: item.x * cellPx,
                        top: item.y * cellPx,
                        width: size.width * cellPx,
                        height: size.height * cellPx,
                        backgroundColor: item.color,
                      }}
                      title={`${item.name}: ${item.width}' x ${item.height}'`}
                    >
                      <span className="line-clamp-2">{item.name}</span>
                    </button>
                  );
                })}
              </div>
              <div
                className="absolute border border-amber-400 bg-amber-50 text-center text-xs font-bold uppercase tracking-wide text-amber-700"
                style={{
                  left: gridWidth + 28,
                  top: 24,
                  width: 24,
                  height: gridHeight,
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                  lineHeight: "24px",
                }}
              >
                Neighbor
              </div>
              <div
                className="absolute border border-teal-400 bg-teal-100 text-center text-xs font-bold uppercase tracking-wide text-teal-800"
                style={{
                  left: 28,
                  top: gridHeight + 24,
                  width: gridWidth,
                  height: 24,
                  lineHeight: "24px",
                }}
              >
                &larr; Front &rarr;
              </div>
            </div>
          </section>

          <aside className="space-y-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm print:hidden">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Selected item
              </h2>
              {selectedItem ? (
                <div className="mt-3 space-y-3">
                  <div>
                    <div className="text-lg font-bold text-slate-950">
                      {selectedItem.name}
                    </div>
                    <div className="text-sm text-slate-500">
                      x {selectedItem.x}' · y {selectedItem.y}' ·{" "}
                      {itemSize(selectedItem).width}' x {itemSize(selectedItem).height}'
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={() => moveSelected(0, -0.5)}
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={() => moveSelected(-0.5, 0)}
                    >
                      Left
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={() => moveSelected(0.5, 0)}
                    >
                      Right
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={() => moveSelected(0, 0.5)}
                    >
                      Down
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={rotateSelected}
                    >
                      Rotate
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={duplicateSelected}
                    >
                      Copy
                    </button>
                  </div>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700"
                    onClick={deleteSelected}
                  >
                    Delete selected
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-500">
                  Select an item on the grid to move, rotate, duplicate, or delete it.
                </p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Layout checks
              </h2>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li>{score >= 85 ? "Pass" : "Check"}: customer path is not too crowded.</li>
                <li>
                  {items.some((item) => /payment|pos|counter/i.test(item.name))
                    ? "Pass"
                    : "Check"}
                  : checkout or payment area is included.
                </li>
                <li>
                  {items.length ? "Pass" : "Check"}: at least one table or display is planned.
                </li>
              </ul>
            </div>
          </aside>
        </section>
      </div>
    </main>
  );
}
